import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { UIState } from '../types';
import { Storage } from '../utils/storage.ts';
import MiniTimer from './MiniTimer.tsx';

declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
      window: Window | null;
    };
  }
}

interface MiniTimerPIPProps {
  isVisible: boolean;
  onClose: () => void;
  currentTaskName?: string | null;
}

const DEFAULT_UI: UIState = { miniTimerPos: { x: 20, y: 20 } };

const MiniTimerPIP: React.FC<MiniTimerPIPProps> = ({ isVisible, onClose, currentTaskName }) => {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [isPipSupported, setIsPipSupported] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Drag state for fallback overlay
  const posRef = useRef<{ x: number; y: number }>(
    Storage.read<UIState>('kf-ui', DEFAULT_UI).miniTimerPos
  );
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const openPipWindow = useCallback(async () => {
    try {
      if (window.documentPictureInPicture) {
        const pip = await window.documentPictureInPicture.requestWindow({ width: 320, height: 180 });

        const allCSS = [...document.styleSheets]
          .map(styleSheet => {
            try {
              return [...styleSheet.cssRules].map(rule => rule.cssText).join('');
            } catch {
              return '';
            }
          })
          .join('\n');

        const style = pip.document.createElement('style');
        style.textContent = allCSS;
        pip.document.head.appendChild(style);

        pip.addEventListener('pagehide', () => {
          setPipWindow(null);
          onClose();
        });

        pip.addEventListener('keydown', (e: KeyboardEvent) => {
          if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey && !e.altKey) {
            onClose();
          }
        });

        setPipWindow(pip);
      }
    } catch (error) {
      console.error('Failed to open Picture-in-Picture window:', error);
      setIsPipSupported(false);
    }
  }, [onClose]);

  const closePipWindow = useCallback(() => {
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
    }
  }, [pipWindow]);

  useEffect(() => {
    setIsPipSupported('documentPictureInPicture' in window);
  }, []);

  useEffect(() => {
    if (isVisible && !pipWindow && isPipSupported) {
      openPipWindow();
    } else if (!isVisible && pipWindow) {
      closePipWindow();
    }
  }, [isVisible, pipWindow, isPipSupported, openPipWindow, closePipWindow]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!overlayRef.current) return;
    e.preventDefault();
    const rect = overlayRef.current.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const onMouseMove = (me: MouseEvent) => {
      if (!dragOffsetRef.current || !overlayRef.current) return;
      const x = Math.max(0, Math.min(window.innerWidth - overlayRef.current.offsetWidth, me.clientX - dragOffsetRef.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - overlayRef.current.offsetHeight, me.clientY - dragOffsetRef.current.y));
      posRef.current = { x, y };
      overlayRef.current.style.left = `${x}px`;
      overlayRef.current.style.top = `${y}px`;
    };

    const onMouseUp = () => {
      dragOffsetRef.current = null;
      Storage.write<UIState>('kf-ui', { miniTimerPos: posRef.current });
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  if (!isVisible) return null;

  const timerContent = (
    <div ref={contentRef} className="mini-timer-container">
      <div className="mini-timer-pip-controls">
        <button className="mini-timer-pip-button close" onClick={onClose} title="Close mini timer">
          ×
        </button>
      </div>
      <MiniTimer currentTaskName={currentTaskName} />
    </div>
  );

  if (isPipSupported && pipWindow) {
    return createPortal(timerContent, pipWindow.document.body);
  }

  return (
    <div className="mini-timer-pip-fallback">
      <div
        ref={overlayRef}
        className="mini-timer-pip"
        style={{
          position: 'fixed',
          left: `${posRef.current.x}px`,
          top: `${posRef.current.y}px`,
          zIndex: 10000,
          cursor: 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        {timerContent}
      </div>
    </div>
  );
};

export default MiniTimerPIP;
